import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './SetFees.css';

const getInitialClassFeeForm = () => ({
  class_id: '',
  amount: '',
  currency: 'PKR',
  fee_type: 'monthly',
  description: '',
  first_fee_date: '',
  expiry_duration: '30'
});

const getInitialAdditionalFees = () => ({
  selectedTypes: [],
  fees: {
    extracurricular: { amount: '', period: 'monthly', name: 'Extracurricular Activities' },
    associated_charges: { amount: '', period: 'monthly', name: 'Associated Charges' },
    other: { amount: '', period: 'monthly', name: '' }
  }
});

function SetFees() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [classes, setClasses] = useState([]);
  const [classFees, setClassFees] = useState({});
  const [academicYear, setAcademicYear] = useState({
    start_date: '',
    end_date: '',
    year_name: ''
  });
  const [feePlan, setFeePlan] = useState([]);
  const [editingFee, setEditingFee] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  
  // Currency options
  const currencyOptions = [
    { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
    { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
    { code: 'QAR', name: 'Qatari Riyal', symbol: '﷼' },
    { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك' },
    { code: 'BHD', name: 'Bahraini Dinar', symbol: 'د.ب' },
    { code: 'OMR', name: 'Omani Rial', symbol: '﷼' },
    { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا' },
    { code: 'LBP', name: 'Lebanese Pound', symbol: 'ل.ل' },
    { code: 'EGP', name: 'Egyptian Pound', symbol: '£' },
    { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
    { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
    { code: 'IRR', name: 'Iranian Rial', symbol: '﷼' },
    { code: 'IQD', name: 'Iraqi Dinar', symbol: 'د.ع' },
    { code: 'AFN', name: 'Afghan Afghani', symbol: '؋' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' }
  ];
  
  // Form states
  const [classFeeForm, setClassFeeForm] = useState(getInitialClassFeeForm());
  const [expiryDurationType, setExpiryDurationType] = useState('30'); // preset value or 'custom'
  const [customExpiryDuration, setCustomExpiryDuration] = useState('');
  
  // Additional fees state
  const [additionalFees, setAdditionalFees] = useState(getInitialAdditionalFees());
  

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!schoolData?.id) return;

    const loadData = async () => {
      const classesData = await fetchClasses();
      await fetchClassFees(classesData);
    };

    loadData();
      fetchAcademicYear();
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    setClassFeeForm(getInitialClassFeeForm());
    setAdditionalFees(getInitialAdditionalFees());
    setExpiryDurationType('30');
    setCustomExpiryDuration('');
    setFeePlan([]);
    setShowEditForm(false);
    setEditingFee(null);
  }, [selectedBranchId]);

  useEffect(() => {
    if (classFeeForm.fee_type && classFeeForm.first_fee_date && classFeeForm.amount && 
        academicYear.start_date && academicYear.end_date) {
      const plan = generateFeePlan(
        classFeeForm.fee_type,
        classFeeForm.first_fee_date,
        academicYear.start_date,
        academicYear.end_date,
        classFeeForm.amount,
        additionalFees
      );
      setFeePlan(plan);
    } else {
      setFeePlan([]);
    }
  }, [classFeeForm.fee_type, classFeeForm.first_fee_date, classFeeForm.amount, 
      academicYear.start_date, academicYear.end_date, additionalFees]);

  const fetchClasses = async () => {
    try {
      setClasses([]);
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('class_name', { ascending: true });

      if (error) {
        console.error('Error fetching classes:', error);
        return [];
      }

      setClasses(data || []);
      return data || [];
    } catch (err) {
      console.error('Error:', err);
      return [];
    }
  };


  const fetchClassFees = async (availableClasses = classes) => {
    try {
      setClassFees({});
      let query = supabase
        .from('class_fees')
        .select(`
          *,
          classes(class_name)
        `)
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        const classIds = (availableClasses || []).map(cls => cls.id).filter(Boolean);
        if (classIds.length === 0) {
          setClassFees({});
          return;
        }
        query = query.in('class_id', classIds);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching class fees:', error);
        return;
      }

      const feesObj = {};
      data?.forEach(fee => {
        feesObj[fee.class_id] = fee;
      });
      setClassFees(feesObj);
    } catch (err) {
      console.error('Error:', err);
    }
  };


  const fetchAcademicYear = async () => {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching academic year:', error);
        return;
      }

      if (data) {
        setAcademicYear({
          start_date: data.start_date || '',
          end_date: data.end_date || '',
          year_name: data.year_name || ''
        });
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const generateFeePlan = (feeType, firstFeeDate, academicYearStart, academicYearEnd, amount, additionalFees) => {
    if (!firstFeeDate || !academicYearStart || !academicYearEnd || !amount) {
      return [];
    }

    const plan = [];
    const startDate = new Date(firstFeeDate);
    const yearStart = new Date(academicYearStart);
    const yearEnd = new Date(academicYearEnd);
    
    // Calculate monthly additional fees
    const monthlyAdditionalFees = additionalFees.selectedTypes
      .filter(type => additionalFees.fees[type].period === 'monthly')
      .reduce((sum, type) => sum + (parseFloat(additionalFees.fees[type].amount) || 0), 0);
    
    // Calculate one-time additional fees
    const oneTimeAdditionalFees = additionalFees.selectedTypes
      .filter(type => additionalFees.fees[type].period === 'one_time')
      .reduce((sum, type) => sum + (parseFloat(additionalFees.fees[type].amount) || 0), 0);
    
    // Calculate monthly breakdown for quarterly fees
    const monthlyAmountForQuarterly = feeType === 'quarterly' ? parseFloat(amount) / 3 : 0;
    const monthlyAmountForSemester = feeType === 'semester' ? parseFloat(amount) / 6 : 0;
    const monthlyAmountForAnnual = feeType === 'annual' ? parseFloat(amount) / 12 : 0;
    
    let currentDate = new Date(startDate);
    let quarterlyCounter = 0;
    let semesterCounter = 0;
    let annualCounter = 0;
    
    switch (feeType) {
      case 'monthly':
        while (currentDate <= yearEnd) {
          const baseAmount = parseFloat(amount);
          const additionalMonthly = monthlyAdditionalFees;
          const isFirstPayment = plan.length === 0;
          const oneTimeFee = isFirstPayment ? oneTimeAdditionalFees : 0;
          
          plan.push({
            date: new Date(currentDate),
            amount: baseAmount + additionalMonthly + oneTimeFee,
            baseAmount: baseAmount,
            additionalMonthly: additionalMonthly,
            oneTimeFee: oneTimeFee,
            type: 'Monthly Fee',
            breakdown: {
              tuition: baseAmount,
              monthlyAdditional: additionalMonthly,
              oneTimeAdditional: oneTimeFee
            }
          });
          currentDate.setMonth(currentDate.getMonth() + 1);
        }
        break;
        
      case 'quarterly':
        // Generate monthly payments with quarterly fee divided by 3
        while (currentDate <= yearEnd) {
          const monthInQuarter = quarterlyCounter % 3;
          let baseAmount = 0;
          let quarterlyFeeLabel = '';
          
          if (monthInQuarter === 0) {
            // First month of quarter - show full quarterly fee
            baseAmount = parseFloat(amount);
            quarterlyFeeLabel = 'Quarterly Fee';
          } else {
            // Other months - show monthly portion
            baseAmount = monthlyAmountForQuarterly;
            quarterlyFeeLabel = `Monthly (Quarterly/${3})`;
          }
          
          const additionalMonthly = monthlyAdditionalFees;
          const isFirstPayment = plan.length === 0;
          const oneTimeFee = isFirstPayment ? oneTimeAdditionalFees : 0;
          
          plan.push({
            date: new Date(currentDate),
            amount: baseAmount + additionalMonthly + oneTimeFee,
            baseAmount: baseAmount,
            additionalMonthly: additionalMonthly,
            oneTimeFee: oneTimeFee,
            type: quarterlyFeeLabel,
            breakdown: {
              tuition: baseAmount,
              monthlyAdditional: additionalMonthly,
              oneTimeAdditional: oneTimeFee
            }
          });
          
          currentDate.setMonth(currentDate.getMonth() + 1);
          quarterlyCounter++;
        }
        break;
        
      case 'semester':
        // Generate monthly payments with semester fee divided by 6
        while (currentDate <= yearEnd) {
          const monthInSemester = semesterCounter % 6;
          let baseAmount = 0;
          let semesterFeeLabel = '';
          
          if (monthInSemester === 0) {
            // First month of semester - show full semester fee
            baseAmount = parseFloat(amount);
            semesterFeeLabel = 'Semester Fee';
          } else {
            // Other months - show monthly portion
            baseAmount = monthlyAmountForSemester;
            semesterFeeLabel = `Monthly (Semester/${6})`;
          }
          
          const additionalMonthly = monthlyAdditionalFees;
          const isFirstPayment = plan.length === 0;
          const oneTimeFee = isFirstPayment ? oneTimeAdditionalFees : 0;
          
          plan.push({
            date: new Date(currentDate),
            amount: baseAmount + additionalMonthly + oneTimeFee,
            baseAmount: baseAmount,
            additionalMonthly: additionalMonthly,
            oneTimeFee: oneTimeFee,
            type: semesterFeeLabel,
            breakdown: {
              tuition: baseAmount,
              monthlyAdditional: additionalMonthly,
              oneTimeAdditional: oneTimeFee
            }
          });
          
          currentDate.setMonth(currentDate.getMonth() + 1);
          semesterCounter++;
        }
        break;
        
      case 'annual':
        // Generate monthly payments with annual fee divided by 12
        while (currentDate <= yearEnd) {
          const monthInYear = annualCounter % 12;
          let baseAmount = 0;
          let annualFeeLabel = '';
          
          if (monthInYear === 0) {
            // First month - show full annual fee
            baseAmount = parseFloat(amount);
            annualFeeLabel = 'Annual Fee';
          } else {
            // Other months - show monthly portion
            baseAmount = monthlyAmountForAnnual;
            annualFeeLabel = `Monthly (Annual/${12})`;
          }
          
          const additionalMonthly = monthlyAdditionalFees;
          const isFirstPayment = plan.length === 0;
          const oneTimeFee = isFirstPayment ? oneTimeAdditionalFees : 0;
          
        plan.push({
            date: new Date(currentDate),
            amount: baseAmount + additionalMonthly + oneTimeFee,
            baseAmount: baseAmount,
            additionalMonthly: additionalMonthly,
            oneTimeFee: oneTimeFee,
            type: annualFeeLabel,
            breakdown: {
              tuition: baseAmount,
              monthlyAdditional: additionalMonthly,
              oneTimeAdditional: oneTimeFee
            }
          });
          
          currentDate.setMonth(currentDate.getMonth() + 1);
          annualCounter++;
        }
        break;
        
      default:
        break;
    }
    
    return plan;
  };

  const handleClassFeeSubmit = async (e) => {
    if (e && e.preventDefault) {
    e.preventDefault();
    }
    try {
      // Save main class fee
      const { error } = await supabase
        .from('class_fees')
        .upsert({
          school_id: schoolData.id,
          class_id: classFeeForm.class_id,
          amount: parseFloat(classFeeForm.amount),
          currency: classFeeForm.currency,
          fee_type: classFeeForm.fee_type,
          description: classFeeForm.description,
          first_fee_date: classFeeForm.first_fee_date,
          expiry_duration: parseInt(classFeeForm.expiry_duration),
          is_active: true,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving class fee:', error);
        alert('Failed to save class fee. Please try again.');
        return;
      }

      // Save additional fees if any are selected
      if (additionalFees.selectedTypes.length > 0 && classFeeForm.class_id) {
        const additionalFeesToSave = additionalFees.selectedTypes.map(feeType => ({
          school_id: schoolData.id,
          class_id: classFeeForm.class_id,
          fee_category: feeType,
          fee_name: additionalFees.fees[feeType].name || (feeType === 'extracurricular' ? 'Extracurricular Activities' : feeType === 'associated_charges' ? 'Associated Charges' : additionalFees.fees.other.name),
          amount: parseFloat(additionalFees.fees[feeType].amount) || 0,
          period: additionalFees.fees[feeType].period,
          currency: classFeeForm.currency,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));

        // Delete existing additional fees for this class first, then insert new ones
        const { error: deleteError } = await supabase
          .from('additional_class_fees')
          .delete()
          .eq('school_id', schoolData.id)
          .eq('class_id', classFeeForm.class_id)
          .in('fee_category', additionalFees.selectedTypes);

        if (deleteError) {
          console.error('Error deleting existing additional fees:', deleteError);
        }

        const { error: additionalError } = await supabase
          .from('additional_class_fees')
          .insert(additionalFeesToSave);

        if (additionalError) {
          console.error('Error saving additional fees:', additionalError);
          alert('Class fee saved, but failed to save additional fees. Please try again.');
        }
      }

      alert('Class fee saved successfully!');
      setClassFeeForm(getInitialClassFeeForm());
      setExpiryDurationType('30');
      setCustomExpiryDuration('');
      setAdditionalFees(getInitialAdditionalFees());
      setFeePlan([]);
      fetchClassFees();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save class fee. Please try again.');
    }
  };


  const handleEditFee = (fee) => {
    console.log('Editing fee:', fee); // Debug log
    setEditingFee(fee);
    const expiryDuration = fee.expiry_duration?.toString() || '30';
    const presetValues = ['7', '15', '30', '45', '60', '90', '120', '180', '365'];
    const isPreset = presetValues.includes(expiryDuration);
    
    setClassFeeForm({
      class_id: fee.class_id,
      amount: fee.amount.toString(),
      currency: fee.currency || 'PKR',
      fee_type: fee.fee_type,
      description: fee.description || '',
      first_fee_date: fee.first_fee_date || '',
      expiry_duration: expiryDuration
    });
    
    if (isPreset) {
      setExpiryDurationType(expiryDuration);
      setCustomExpiryDuration('');
    } else {
      setExpiryDurationType('custom');
      setCustomExpiryDuration(expiryDuration);
    }
    
    setShowEditForm(true);
  };

  const handleUpdateFee = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('class_fees')
        .update({
          amount: parseFloat(classFeeForm.amount),
          currency: classFeeForm.currency,
          fee_type: classFeeForm.fee_type,
          description: classFeeForm.description,
          first_fee_date: classFeeForm.first_fee_date,
          expiry_duration: parseInt(classFeeForm.expiry_duration),
          updated_at: new Date().toISOString()
        })
        .eq('id', editingFee.id);

      if (error) {
        console.error('Error updating class fee:', error);
        alert('Failed to update class fee. Please try again.');
        return;
      }

      alert('Class fee updated successfully!');
      setShowEditForm(false);
      setEditingFee(null);
      setClassFeeForm(getInitialClassFeeForm());
      setExpiryDurationType('30');
      setCustomExpiryDuration('');
      setFeePlan([]);
      fetchClassFees();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update class fee. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingFee(null);
    setClassFeeForm(getInitialClassFeeForm());
    setExpiryDurationType('30');
    setCustomExpiryDuration('');
    setFeePlan([]);
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>💰 Set Fees</h1>
          <p>Manage class fees</p>
        </div>
          <div className="branch-filter-row">
            <BranchSelector
              schoolId={schoolData?.id}
              selectedBranchId={selectedBranchId}
              onBranchChange={handleBranchChange}
            />
            <span className="branch-context-text">
              {selectedBranchId
                ? 'Showing data for the selected branch'
                : 'Showing data for all branches'}
            </span>
        </div>

        <div className="fees-container">
          {/* Class Fees Section */}
          <div className="class-fees-section">
            <div className="section-card">
              <h2>📚 Class Fee Management</h2>
              <p className="section-description">
                Set standard fees for each class. These will be used as default amounts for students.
              </p>
              
              <form onSubmit={showEditForm ? handleUpdateFee : handleClassFeeSubmit} className="class-fee-form">
                {/* Academic Year Display */}
                {academicYear.year_name && (
                  <div className="academic-year-info">
                    <h3>📅 Current Academic Year: {academicYear.year_name}</h3>
                    <p>
                      <strong>Start Date:</strong> {new Date(academicYear.start_date).toLocaleDateString()} | 
                      <strong> End Date:</strong> {new Date(academicYear.end_date).toLocaleDateString()}
                    </p>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="class-select">Select Class *</label>
                    <select
                      id="class-select"
                      value={classFeeForm.class_id}
                      onChange={(e) => setClassFeeForm({...classFeeForm, class_id: e.target.value})}
                      className="form-select"
                      required
                      disabled={showEditForm}
                    >
                      <option value="">Choose a class...</option>
                      {classes.map((cls) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.class_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="fee-amount">Fee Amount *</label>
                    <div className="amount-input-container">
                      <input
                        type="number"
                        id="fee-amount"
                        value={classFeeForm.amount}
                        onChange={(e) => setClassFeeForm({...classFeeForm, amount: e.target.value})}
                        placeholder="Enter amount"
                        className="form-input amount-input"
                        min="0"
                        step="0.01"
                        required
                      />
                      <select
                        value={classFeeForm.currency}
                        onChange={(e) => setClassFeeForm({...classFeeForm, currency: e.target.value})}
                        className="currency-select"
                        required
                      >
                        {currencyOptions.map((currency) => (
                          <option key={currency.code} value={currency.code}>
                            {currency.code} - {currency.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fee-type">Fee Type *</label>
                    <select
                      id="fee-type"
                      value={classFeeForm.fee_type}
                      onChange={(e) => setClassFeeForm({...classFeeForm, fee_type: e.target.value})}
                      className="form-select"
                      required
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semester">Semester</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="first-fee-date">First Date of Fee *</label>
                    <input
                      type="date"
                      id="first-fee-date"
                      value={classFeeForm.first_fee_date}
                      onChange={(e) => setClassFeeForm({...classFeeForm, first_fee_date: e.target.value})}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="expiry-duration-type">Expiry Duration (Days) *</label>
                    <select
                      id="expiry-duration-type"
                      value={expiryDurationType === 'custom' ? 'custom' : (['7', '15', '30', '45', '60', '90', '120', '180', '365'].includes(classFeeForm.expiry_duration) ? classFeeForm.expiry_duration : 'custom')}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        if (selectedValue !== 'custom') {
                          setExpiryDurationType(selectedValue);
                          setClassFeeForm({...classFeeForm, expiry_duration: selectedValue});
                          setCustomExpiryDuration('');
                        } else {
                          setExpiryDurationType('custom');
                          setClassFeeForm({...classFeeForm, expiry_duration: customExpiryDuration || ''});
                        }
                      }}
                      className="form-select"
                      required
                      style={{ marginBottom: expiryDurationType === 'custom' ? '10px' : '0' }}
                    >
                      <option value="7">7 Days</option>
                      <option value="15">15 Days</option>
                      <option value="30">30 Days</option>
                      <option value="45">45 Days</option>
                      <option value="60">60 Days</option>
                      <option value="90">90 Days</option>
                      <option value="120">120 Days</option>
                      <option value="180">180 Days</option>
                      <option value="365">365 Days (1 Year)</option>
                      <option value="custom">Custom (Enter Days)</option>
                    </select>
                    {expiryDurationType === 'custom' && (
                      <input
                        type="number"
                        id="custom-expiry-duration"
                        value={customExpiryDuration}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCustomExpiryDuration(value);
                          setClassFeeForm({...classFeeForm, expiry_duration: value});
                        }}
                        placeholder="Enter number of days"
                        className="form-input"
                        min="1"
                        step="1"
                        required
                        style={{ marginTop: '10px' }}
                      />
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="expiry-info">Expiry Information</label>
                    <div className="expiry-info-display">
                      <p>Fees will expire after <strong>{classFeeForm.expiry_duration || '0'} days</strong> from the due date</p>
                      <p className="expiry-note">After expiry, students will need to pay late fees or penalties</p>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="fee-description">Description</label>
                  <textarea
                    id="fee-description"
                    value={classFeeForm.description}
                    onChange={(e) => setClassFeeForm({...classFeeForm, description: e.target.value})}
                    placeholder="Additional details about the fee..."
                    className="form-textarea"
                    rows="3"
                  />
                </div>

                {/* Fee Plan Preview */}
                {feePlan.length > 0 && (
                  <div className="fee-plan-preview">
                    <h3>📋 Fee Plan Preview (Combined Monthly Breakdown)</h3>
                    <p className="plan-description">
                      Based on your selections, here's the complete fee schedule for the academic year with all fees combined:
                    </p>
                    <div className="fee-plan-grid">
                      {feePlan.map((item, index) => (
                        <div key={index} className="fee-plan-item" style={{ textAlign: 'left', padding: '1rem' }}>
                          <div className="fee-date" style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#1F2937' }}>
                            {item.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                          <div className="fee-amount" style={{ fontSize: '1.2rem', fontWeight: '700', color: '#059669', marginBottom: '0.75rem' }}>
                            {classFeeForm.currency} {item.amount.toLocaleString()}
                          </div>
                          <div className="fee-type" style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '0.5rem', textTransform: 'none' }}>
                            {item.type}
                          </div>
                          {item.breakdown && (
                            <div className="fee-breakdown" style={{ 
                              marginTop: '0.75rem', 
                              paddingTop: '0.75rem', 
                              borderTop: '1px solid #E5E7EB',
                              fontSize: '0.8rem',
                              color: '#4B5563'
                            }}>
                              <div style={{ marginBottom: '0.25rem' }}>
                                <span style={{ fontWeight: '500' }}>Tuition:</span> {classFeeForm.currency} {item.breakdown.tuition.toLocaleString()}
                        </div>
                              {item.breakdown.monthlyAdditional > 0 && (
                                <div style={{ marginBottom: '0.25rem' }}>
                                  <span style={{ fontWeight: '500' }}>Monthly Additional:</span> {classFeeForm.currency} {item.breakdown.monthlyAdditional.toLocaleString()}
                    </div>
                              )}
                              {item.breakdown.oneTimeAdditional > 0 && (
                                <div style={{ marginBottom: '0.25rem', color: '#DC2626', fontWeight: '500' }}>
                                  <span>One-Time Fee:</span> {classFeeForm.currency} {item.breakdown.oneTimeAdditional.toLocaleString()}
                  </div>
                  )}
                </div>
                          )}
                      </div>
                    ))}
                  </div>
                    <div className="total-summary" style={{ 
                      background: '#ECFDF5', 
                      border: '2px solid #10B981', 
                      borderRadius: '8px', 
                      padding: '1rem', 
                      textAlign: 'center',
                      marginTop: '1rem'
                    }}>
                      <strong style={{ fontSize: '1.1rem', color: '#047857' }}>
                        Total Fees: {classFeeForm.currency} {feePlan.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
                      </strong>
              </div>
            </div>
                )}
              </form>

              {/* Add Other Fields Section */}
              <div className="additional-fees-section" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #E5E7EB' }}>
                <h2 style={{ marginBottom: '1rem', color: '#1F2937' }}>Add Other Fields</h2>
                <p className="section-description" style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
                  Add additional fees such as extracurricular activities, associated charges, or custom fees.
              </p>

                {/* Fee Type Selection */}
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#374151' }}>
                    Select Fee Types (Multiple Selection)
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {[
                      { value: 'extracurricular', label: 'Extracurricular Activities' },
                      { value: 'associated_charges', label: 'Associated Charges' },
                      { value: 'other', label: 'Other' }
                    ].map((option) => (
                      <label
                        key={option.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: additionalFees.selectedTypes.includes(option.value) ? '#EFF6FF' : '#FFFFFF',
                          transition: 'all 0.2s ease'
                        }}
                      >
                  <input
                          type="checkbox"
                          checked={additionalFees.selectedTypes.includes(option.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAdditionalFees(prev => ({
                                ...prev,
                                selectedTypes: [...prev.selectedTypes, option.value]
                              }));
                            } else {
                              setAdditionalFees(prev => ({
                                ...prev,
                                selectedTypes: prev.selectedTypes.filter(t => t !== option.value),
                                fees: {
                                  ...prev.fees,
                                  [option.value]: {
                                    amount: '',
                                    period: 'monthly',
                                    name: option.value === 'other' ? '' : prev.fees[option.value].name
                                  }
                                }
                              }));
                            }
                          }}
                          style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                        <span style={{ fontWeight: '500', color: '#1F2937' }}>{option.label}</span>
                      </label>
                    ))}
                </div>
              </div>

                {/* Fee Details for Selected Types */}
                {additionalFees.selectedTypes.length > 0 && (
                  <div className="additional-fees-details" style={{ marginTop: '2rem' }}>
                    <h3 style={{ marginBottom: '1rem', color: '#374151', fontSize: '1.1rem' }}>Fee Details</h3>
                    {additionalFees.selectedTypes.map((feeType) => (
                      <div
                        key={feeType}
                        style={{
                          padding: '1.5rem',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          marginBottom: '1rem',
                          backgroundColor: '#FAFAFA'
                        }}
                      >
                        <h4 style={{ marginBottom: '1rem', color: '#1F2937', fontSize: '1rem' }}>
                          {feeType === 'extracurricular' ? 'Extracurricular Activities' :
                           feeType === 'associated_charges' ? 'Associated Charges' : 'Other Fee'}
                        </h4>

                        {/* Name field for "Other" */}
                        {feeType === 'other' && (
                          <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                              Fee Name <span style={{ color: '#EF4444' }}>*</span>
                            </label>
                          <input
                              type="text"
                              value={additionalFees.fees.other.name}
                              onChange={(e) => setAdditionalFees(prev => ({
                                ...prev,
                                fees: {
                                  ...prev.fees,
                                  other: { ...prev.fees.other, name: e.target.value }
                                }
                              }))}
                              placeholder="Enter fee name"
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #D1D5DB',
                                borderRadius: '6px',
                                fontSize: '0.95rem'
                              }}
                            required
                          />
                        </div>
                        )}
                      
                        <div className="additional-fees-grid">
                          {/* Fee Amount */}
                      <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                              Fee Amount <span style={{ color: '#EF4444' }}>*</span>
                            </label>
                        <input
                          type="number"
                              value={additionalFees.fees[feeType].amount}
                              onChange={(e) => setAdditionalFees(prev => ({
                                ...prev,
                                fees: {
                                  ...prev.fees,
                                  [feeType]: { ...prev.fees[feeType], amount: e.target.value }
                                }
                              }))}
                              placeholder="Enter amount"
                          min="0"
                          step="0.01"
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #D1D5DB',
                                borderRadius: '6px',
                                fontSize: '0.95rem'
                              }}
                              required
                            />
                    </div>

                          {/* Period Selection */}
                      <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                              Period <span style={{ color: '#EF4444' }}>*</span>
                            </label>
                        <select
                              value={additionalFees.fees[feeType].period}
                              onChange={(e) => setAdditionalFees(prev => ({
                                ...prev,
                                fees: {
                                  ...prev.fees,
                                  [feeType]: { ...prev.fees[feeType], period: e.target.value }
                                }
                              }))}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #D1D5DB',
                                borderRadius: '6px',
                                fontSize: '0.95rem',
                                backgroundColor: '#FFFFFF'
                              }}
                          required
                        >
                          <option value="monthly">Monthly</option>
                              <option value="one_time">1 Time</option>
                        </select>
                      </div>
                      </div>
                    </div>
                    ))}
                    </div>
                )}

                {/* Save Button - Moved below Additional Fees Section */}
                <div className="form-actions" style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '2px solid #E5E7EB' }}>
                  <button 
                    type="button" 
                    onClick={(e) => {
                      // Validate main form first
                      const form = document.querySelector('.class-fee-form');
                      if (form && form.checkValidity()) {
                        handleClassFeeSubmit(e);
                      } else {
                        form?.reportValidity();
                      }
                    }} 
                    className="btn-primary"
                    style={{ 
                      padding: '0.75rem 2rem',
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}
                  >
                    {showEditForm ? '💾 Update Class Fee' : '💾 Save Class Fee'}
                      </button>
                  {showEditForm && (
                    <button type="button" onClick={handleCancelEdit} className="btn-secondary">
                      ❌ Cancel Edit
                      </button>
                  )}
                  </div>
                    </div>
                          </div>
                          </div>
                        </div>
      </div>
    </div>
  );
}

export default SetFees;
